import { useEffect, useState } from "react";
import { X, Share, PlusSquare, MoreVertical, Smartphone, Download } from "lucide-react";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const detectPlatform = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  return "android";
};

export const InstallGuide = () => {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState(detectPlatform());

  useEffect(() => {
    if (!isStandalone() && !localStorage.getItem("gt_install_guide_seen")) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-install-guide", handler);
    return () => window.removeEventListener("open-install-guide", handler);
  }, []);

  const close = () => {
    localStorage.setItem("gt_install_guide_seen", "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div data-testid="install-guide-modal" className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md bg-[#14141A] border border-white/15 p-5 pb-8 sm:pb-5 animate-[slideUp_.3s_ease-out]">
        <button data-testid="install-guide-close-button" onClick={close}
          className="absolute top-3 right-3 text-[#B0B0B4] hover:text-white p-2 transition-colors" aria-label="Fermer">
          <X size={20} />
        </button>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 bg-[#FF3B30] flex items-center justify-center">
            <Smartphone size={24} className="text-white" />
          </div>
          <div>
            <h2 className="font-display text-2xl text-white leading-none">INSTALLE GYMTRACK</h2>
            <p className="text-[#B0B0B4] text-xs mt-0.5">Comme une vraie app, sur ton écran d'accueil</p>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          {[{ id: "ios", label: "iPhone" }, { id: "android", label: "Android" }].map((p) => (
            <button key={p.id} data-testid={`install-guide-${p.id}-tab`} onClick={() => setPlatform(p.id)}
              className={`flex-1 h-9 text-xs uppercase tracking-wider transition-colors ${
                platform === p.id ? "bg-[#FF3B30] text-white" : "bg-[#14141A] text-[#B0B0B4] hover:text-white"}`}>
              {p.label}
            </button>
          ))}
        </div>

        <ol className="mt-4 space-y-3 text-sm text-[#ccc]">
          {platform === "ios" ? (
            <>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 shrink-0 bg-[#14141A] border border-white/15 flex items-center justify-center text-xs font-mono text-[#FF3B30]">1</span>
                <span>Ouvre GymTrack dans <strong className="text-white">Safari</strong></span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 shrink-0 bg-[#14141A] border border-white/15 flex items-center justify-center text-xs font-mono text-[#FF3B30]">2</span>
                <span>Touche le bouton <strong className="text-white">Partager</strong> <Share size={15} className="inline text-[#FF3B30] -mt-0.5" /> en bas de l'écran</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 shrink-0 bg-[#14141A] border border-white/15 flex items-center justify-center text-xs font-mono text-[#FF3B30]">3</span>
                <span>Choisis <strong className="text-white">« Sur l'écran d'accueil »</strong> <PlusSquare size={15} className="inline text-[#FF3B30] -mt-0.5" /> puis <strong className="text-white">Ajouter</strong></span>
              </li>
            </>
          ) : (
            <>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 shrink-0 bg-[#14141A] border border-white/15 flex items-center justify-center text-xs font-mono text-[#FF3B30]">1</span>
                <span>Ouvre GymTrack dans <strong className="text-white">Chrome</strong></span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 shrink-0 bg-[#14141A] border border-white/15 flex items-center justify-center text-xs font-mono text-[#FF3B30]">2</span>
                <span>Touche le menu <strong className="text-white">⋮</strong> <MoreVertical size={15} className="inline text-[#FF3B30] -mt-0.5" /> en haut à droite</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-6 h-6 shrink-0 bg-[#14141A] border border-white/15 flex items-center justify-center text-xs font-mono text-[#FF3B30]">3</span>
                <span>Choisis <strong className="text-white">« Ajouter à l'écran d'accueil »</strong> puis <strong className="text-white">Installer</strong></span>
              </li>
            </>
          )}
        </ol>

        <p className="mt-4 text-[11px] text-[#B0B0B4] flex items-center gap-2">
          <Download size={14} className="text-[#32D74B]" />
          Une fois installée, l'app fonctionne même hors ligne à la salle.
        </p>

        <button data-testid="install-guide-done-button" onClick={close}
          className="mt-4 w-full h-11 bg-[#FF3B30] text-white font-display text-xl tracking-wide hover:bg-[#e0332a] transition-colors">
          C'EST COMPRIS
        </button>
      </div>
    </div>
  );
};
